import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  BanknotesIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  DocumentCheckIcon,
  IdentificationIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  PlusIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import cashService from '../../services/cashService';
import accountService from '../../services/accountService';
import agencyService from '../../services/agencyService';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';

const today = new Date().toISOString().slice(0, 10);
const currentMonth = new Date().toISOString().slice(0, 7);

const CashRegister = () => {
  const { formatCurrency } = useCurrency();
  const { showSuccess, showError } = useToast();
  const { hasPermission } = useAuth();
  const [session, setSession] = useState(null);
  const [dailyReport, setDailyReport] = useState(null);
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [registers, setRegisters] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [date, setDate] = useState(today);
  const [month, setMonth] = useState(currentMonth);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('session');
  const [openForm, setOpenForm] = useState({ cash_register_id: '', opening_amount: '', notes: '' });
  const [registerForm, setRegisterForm] = useState({ agency_id: '', code: '', name: '', description: '' });
  const [closeForm, setCloseForm] = useState({ closing_amount: '', notes: '' });
  const [movementForm, setMovementForm] = useState({ movement_type: 'manual_in', amount: '', description: '', reference: '' });
  const [accountQuery, setAccountQuery] = useState('');
  const [accountResults, setAccountResults] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [identityConfirmed, setIdentityConfirmed] = useState(false);
  const [transactionForm, setTransactionForm] = useState({ type: 'deposit', amount: '', description: '', reference: '' });

  const expectedBalance = useMemo(() => session?.expected_balance || 0, [session]);
  const canDeposit = hasPermission('process_deposit');
  const canWithdraw = hasPermission('process_withdrawal');
  const canViewReports = hasPermission('view_reports');
  const canManageRegisters = hasPermission('manage_agencies');

  const fetchCurrent = async () => {
    try {
      const data = await cashService.getCurrentSession();
      setSession(data.session);
      if (data.session) {
        setCloseForm((prev) => ({ ...prev, closing_amount: data.session.expected_balance || 0 }));
      }
    } catch (error) {
      showError('Erreur lors du chargement de la caisse');
    } finally {
      setLoading(false);
    }
  };

  const fetchRegisters = async () => {
    const data = await cashService.getRegisters();
    setRegisters(data);
    setOpenForm((prev) => ({
      ...prev,
      cash_register_id: prev.cash_register_id || data[0]?.id || '',
    }));
  };

  const fetchAgencies = async () => {
    if (!canManageRegisters) return;
    const data = await agencyService.getAllAgencies();
    const list = Array.isArray(data) ? data : data.agencies || [];
    setAgencies(list);
    setRegisterForm((prev) => ({ ...prev, agency_id: prev.agency_id || list[0]?.id || '' }));
  };

  const fetchDailyReport = async () => {
    const data = await cashService.getDailyReport(date);
    setDailyReport(data);
  };

  const fetchMonthlyReport = async () => {
    const data = await cashService.getMonthlyReport(month);
    setMonthlyReport(data);
  };

  useEffect(() => {
    fetchCurrent();
    fetchRegisters();
    fetchAgencies();
  }, []);

  useEffect(() => {
    if (canViewReports) {
      fetchDailyReport();
    }
  }, [date, canViewReports]);

  useEffect(() => {
    if (canViewReports) {
      fetchMonthlyReport();
    }
  }, [month, canViewReports]);

  useEffect(() => {
    if (!canDeposit && canWithdraw) {
      setTransactionForm((prev) => ({ ...prev, type: 'withdraw' }));
    }
  }, [canDeposit, canWithdraw]);

  useEffect(() => {
    if (!session) return undefined;
    const intervalId = window.setInterval(() => {
      fetchCurrent();
    }, 30000);
    return () => window.clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id]);

  const handleOpen = async (e) => {
    e.preventDefault();
    try {
      await cashService.openSession({
        cash_register_id: Number(openForm.cash_register_id),
        opening_amount: Number(openForm.opening_amount || 0),
        notes: openForm.notes,
      });
      showSuccess('Caisse ouverte');
      setOpenForm((prev) => ({ cash_register_id: prev.cash_register_id, opening_amount: '', notes: '' }));
      fetchCurrent();
      if (canViewReports) fetchDailyReport();
    } catch (error) {
      showError(error.response?.data?.message || "Erreur lors de l'ouverture");
    }
  };

  const handleCreateRegister = async (e) => {
    e.preventDefault();
    try {
      await cashService.createRegister({
        ...registerForm,
        agency_id: registerForm.agency_id ? Number(registerForm.agency_id) : undefined,
      });
      showSuccess('Caisse agence créée');
      setRegisterForm({ agency_id: agencies[0]?.id || '', code: '', name: '', description: '' });
      fetchRegisters();
    } catch (error) {
      showError(error.response?.data?.message || 'Erreur lors de la création de la caisse');
    }
  };

  const handleToggleRegister = async (register) => {
    try {
      await cashService.updateRegister(register.id, {
        ...register,
        is_active: !register.is_active,
      });
      showSuccess(register.is_active ? 'Caisse désactivée' : 'Caisse activée');
      fetchRegisters();
      fetchCurrent();
    } catch (error) {
      showError(error.response?.data?.message || 'Erreur lors de la mise à jour de la caisse');
    }
  };

  const handleClose = async (e) => {
    e.preventDefault();
    try {
      await cashService.closeSession({
        closing_amount: Number(closeForm.closing_amount || 0),
        notes: closeForm.notes,
      });
      showSuccess('Caisse fermée');
      setCloseForm({ closing_amount: '', notes: '' });
      fetchCurrent();
      if (canViewReports) fetchDailyReport();
      if (canViewReports) fetchMonthlyReport();
    } catch (error) {
      showError(error.response?.data?.message || 'Erreur lors de la fermeture');
    }
  };

  const handleMovement = async (e) => {
    e.preventDefault();
    try {
      await cashService.createMovement({
        ...movementForm,
        amount: Number(movementForm.amount || 0),
      });
      showSuccess('Mouvement enregistré');
      setMovementForm({ movement_type: 'manual_in', amount: '', description: '', reference: '' });
      fetchCurrent();
      if (canViewReports) fetchDailyReport();
      if (canViewReports) fetchMonthlyReport();
    } catch (error) {
      showError(error.response?.data?.message || 'Erreur lors du mouvement');
    }
  };

  const handleAccountLookup = async (e) => {
    e.preventDefault();
    if (accountQuery.trim().length < 3) {
      showError('Entrez au moins 3 caractères');
      return;
    }

    try {
      const data = await accountService.lookupAccount(accountQuery.trim());
      setAccountResults(data);
      setSelectedAccount(data[0] || null);
      setIdentityConfirmed(false);
      if (!data.length) {
        showError('Aucun compte trouvé');
      }
    } catch (error) {
      showError(error.response?.data?.message || 'Erreur lors de la recherche du compte');
    }
  };

  const handleClientTransaction = async (e) => {
    e.preventDefault();
    if (!selectedAccount) {
      showError('Sélectionnez un compte');
      return;
    }
    if (!session) {
      showError('Ouvrez la caisse avant de traiter une transaction');
      return;
    }
    if (!identityConfirmed) {
      showError("Confirmez d'abord la vérification d'identité du client");
      return;
    }

    try {
      const payload = {
        amount: Number(transactionForm.amount || 0),
        description: transactionForm.description,
        reference: transactionForm.reference,
      };
      if (transactionForm.type === 'deposit') {
        await accountService.deposit(selectedAccount.id, payload);
        showSuccess('Dépôt effectué');
      } else {
        await accountService.withdraw(selectedAccount.id, payload);
        showSuccess('Retrait effectué');
      }
      setTransactionForm({ type: 'deposit', amount: '', description: '', reference: '' });
      setSelectedAccount(null);
      setIdentityConfirmed(false);
      setAccountResults([]);
      setAccountQuery('');
      fetchCurrent();
      if (canViewReports) fetchDailyReport();
      if (canViewReports) fetchMonthlyReport();
    } catch (error) {
      showError(error.response?.data?.message || 'Erreur lors de la transaction');
    }
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExportReport = async (period, format) => {
    try {
      const blob = period === 'daily'
        ? await cashService.exportDailyReport(date, format)
        : await cashService.exportMonthlyReport(month, format);
      const extension = format === 'pdf' ? 'pdf' : 'xlsx';
      const label = period === 'daily' ? date.replaceAll('-', '') : month.replaceAll('-', '');
      downloadBlob(blob, `rapport_caisse_${period}_${label}.${extension}`);
    } catch (error) {
      showError(error.response?.data?.message || "Erreur lors de l'export du rapport");
    }
  };

  const Stat = ({ label, value, icon: Icon, tone = 'gray' }) => (
    <div className="card">
      <div className="p-5 flex items-center">
        <div className={`rounded-md p-3 ${{
          green: 'bg-green-100',
          red: 'bg-red-100',
          blue: 'bg-blue-100',
          gray: 'bg-gray-100',
        }[tone] || 'bg-gray-100'}`}>
          <Icon className={`h-6 w-6 ${{
            green: 'text-green-600',
            red: 'text-red-600',
            blue: 'text-blue-600',
            gray: 'text-gray-600',
          }[tone] || 'text-gray-600'}`} />
        </div>
        <div className="ml-4">
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-xl font-semibold text-gray-900">{value}</p>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return <div className="p-6 text-sm text-gray-500">Chargement de la caisse...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Caisse</h1>
          <p className="mt-1 text-sm text-gray-600">Ouverture, fermeture et rapports de clôture</p>
        </div>
        <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${session ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
          {session ? 'Caisse ouverte' : 'Caisse fermée'}
        </span>
      </div>
      {session?.cash_register_name && (
        <div className="rounded-md bg-blue-50 px-4 py-3 text-sm text-blue-800">
          Session ouverte sur {session.cash_register_code} - {session.cash_register_name}
        </div>
      )}

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            ['session', 'Session'],
            ['transaction', 'Transaction client'],
            ['registers', 'Caisses agence'],
            ...(canViewReports ? [
              ['daily', 'Rapport journalier'],
              ['monthly', 'Rapport mensuel'],
            ] : []),
          ].map(([id, label]) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`py-4 px-1 border-b-2 text-sm font-medium ${activeTab === id ? 'border-primary-500 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              {label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'session' && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            {session ? (
              <>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <Stat label="Entrées" value={formatCurrency(session.total_in)} icon={ArrowDownTrayIcon} tone="green" />
                  <Stat label="Sorties" value={formatCurrency(session.total_out)} icon={ArrowUpTrayIcon} tone="red" />
                  <Stat label="Solde attendu" value={formatCurrency(expectedBalance)} icon={BanknotesIcon} tone="blue" />
                </div>

                <div className="card">
                  <div className="p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Mouvements de la session</h3>
                    <div className="overflow-hidden border border-gray-200 rounded-lg">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Heure</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Montant</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {(session.movements || []).map((movement) => (
                            <tr key={movement.id}>
                              <td className="px-4 py-3 text-sm text-gray-500">{new Date(movement.created_at).toLocaleTimeString('fr-FR')}</td>
                              <td className="px-4 py-3 text-sm text-gray-900">{movement.movement_type}</td>
                              <td className="px-4 py-3 text-sm text-gray-500">{movement.description || '-'}</td>
                              <td className={`px-4 py-3 text-sm text-right font-medium ${movement.direction === 'in' ? 'text-green-700' : 'text-red-700'}`}>
                                {movement.direction === 'in' ? '+' : '-'} {formatCurrency(movement.amount)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="card">
                <form onSubmit={handleOpen} className="p-6 space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">Ouvrir la caisse</h3>
                  <select className="input-field" value={openForm.cash_register_id} onChange={(e) => setOpenForm({ ...openForm, cash_register_id: e.target.value })}>
                    <option value="">Sélectionnez une caisse agence</option>
                    {registers.filter((register) => register.is_active !== false).map((register) => (
                      <option key={register.id} value={register.id}>
                        {register.code} - {register.name}
                      </option>
                    ))}
                  </select>
                  <input className="input-field" type="number" min="0" step="0.01" placeholder="Montant d'ouverture" value={openForm.opening_amount} onChange={(e) => setOpenForm({ ...openForm, opening_amount: e.target.value })} />
                  <textarea className="input-field" rows={3} placeholder="Notes" value={openForm.notes} onChange={(e) => setOpenForm({ ...openForm, notes: e.target.value })} />
                  <button className="btn-primary" type="submit" disabled={!openForm.cash_register_id}>
                    <CheckCircleIcon className="h-5 w-5 mr-2" />
                    Ouvrir
                  </button>
                </form>
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div className="card">
              <form onSubmit={handleMovement} className="p-6 space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Mouvement manuel</h3>
                <select className="input-field" value={movementForm.movement_type} disabled={!session} onChange={(e) => setMovementForm({ ...movementForm, movement_type: e.target.value })}>
                  <option value="manual_in">Entrée</option>
                  <option value="manual_out">Sortie</option>
                </select>
                <input className="input-field" type="number" min="0" step="0.01" placeholder="Montant" disabled={!session} value={movementForm.amount} onChange={(e) => setMovementForm({ ...movementForm, amount: e.target.value })} />
                <input className="input-field" placeholder="Référence" disabled={!session} value={movementForm.reference} onChange={(e) => setMovementForm({ ...movementForm, reference: e.target.value })} />
                <textarea className="input-field" rows={3} placeholder="Description" disabled={!session} value={movementForm.description} onChange={(e) => setMovementForm({ ...movementForm, description: e.target.value })} />
                <button className="btn-secondary w-full" type="submit" disabled={!session || !movementForm.amount}>
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Enregistrer
                </button>
              </form>
            </div>

            <div className="card">
              <form onSubmit={handleClose} className="p-6 space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Fermer la caisse</h3>
                <input className="input-field" type="number" min="0" step="0.01" placeholder="Montant compté" disabled={!session} value={closeForm.closing_amount} onChange={(e) => setCloseForm({ ...closeForm, closing_amount: e.target.value })} />
                <textarea className="input-field" rows={3} placeholder="Notes de clôture" disabled={!session} value={closeForm.notes} onChange={(e) => setCloseForm({ ...closeForm, notes: e.target.value })} />
                <button className="btn-primary w-full" type="submit" disabled={!session || closeForm.closing_amount === ''}>
                  <LockClosedIcon className="h-5 w-5 mr-2" />
                  Fermer
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'registers' && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 card">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Caisses par agence</h3>
              <div className="space-y-3">
                {registers.map((register) => (
                  <div key={register.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{register.code} - {register.name}</p>
                        <p className="text-sm text-gray-500">{register.description || 'Aucune description'}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${register.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {register.is_active ? 'Active' : 'Inactive'}
                        </span>
                        {canManageRegisters && (
                          <button
                            type="button"
                            onClick={() => handleToggleRegister(register)}
                            className="text-sm text-primary-600 hover:text-primary-900"
                          >
                            {register.is_active ? 'Désactiver' : 'Activer'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                {registers.length === 0 && (
                  <p className="text-sm text-gray-500">Aucune caisse créée pour cette agence.</p>
                )}
              </div>
            </div>
          </div>

          {canManageRegisters && (
            <div className="card">
              <form onSubmit={handleCreateRegister} className="p-6 space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Créer une caisse</h3>
                {agencies.length > 0 && (
                  <select className="input-field" value={registerForm.agency_id} onChange={(e) => setRegisterForm({ ...registerForm, agency_id: e.target.value })}>
                    {agencies.map((agency) => (
                      <option key={agency.id} value={agency.id}>{agency.name}</option>
                    ))}
                  </select>
                )}
                <input className="input-field" placeholder="Code caisse, ex: CS-01" value={registerForm.code} onChange={(e) => setRegisterForm({ ...registerForm, code: e.target.value })} />
                <input className="input-field" placeholder="Nom caisse" value={registerForm.name} onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })} />
                <textarea className="input-field" rows={3} placeholder="Description" value={registerForm.description} onChange={(e) => setRegisterForm({ ...registerForm, description: e.target.value })} />
                <button className="btn-primary w-full" type="submit" disabled={!registerForm.code || !registerForm.name}>
                  Créer
                </button>
              </form>
            </div>
          )}
        </div>
      )}

      {activeTab === 'transaction' && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <div className="card">
              <form onSubmit={handleAccountLookup} className="p-6">
                <h3 className="text-lg font-medium text-gray-900">Accéder à un compte</h3>
                <div className="mt-4 flex gap-3">
                  <input
                    className="input-field"
                    placeholder="Numéro de compte, numéro client, nom, téléphone..."
                    value={accountQuery}
                    onChange={(e) => setAccountQuery(e.target.value)}
                  />
                  <button className="btn-primary" type="submit">
                    <MagnifyingGlassIcon className="h-5 w-5 mr-2" />
                    Rechercher
                  </button>
                </div>
              </form>
            </div>

            {accountResults.length > 0 && (
              <div className="card">
                <div className="p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Résultats</h3>
                  <div className="space-y-3">
                    {accountResults.map((account) => (
                      <div key={account.id} className={`border rounded-lg ${selectedAccount?.id === account.id ? 'border-primary-500 bg-primary-50' : 'border-gray-200'}`}>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedAccount(account);
                            setIdentityConfirmed(false);
                          }}
                          className="w-full text-left p-4 hover:bg-gray-50"
                        >
                          <div className="flex justify-between">
                            <div>
                              <p className="font-medium text-gray-900">{account.account_number}</p>
                              <p className="text-sm text-gray-500">
                                {account.client?.first_name} {account.client?.last_name} · {account.client?.client_number}
                              </p>
                            </div>
                            <p className="font-semibold text-gray-900">{formatCurrency(account.balance)}</p>
                          </div>
                        </button>
                        {selectedAccount?.id === account.id && (
                          <div className="border-t border-primary-100 p-4">
                            <ClientVerificationCard
                              account={account}
                              formatCurrency={formatCurrency}
                              identityConfirmed={identityConfirmed}
                              setIdentityConfirmed={setIdentityConfirmed}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="card">
            <form onSubmit={handleClientTransaction} className="p-6 space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Transaction</h3>
              {!session && (
                <div className="rounded-md bg-yellow-50 p-3 text-sm text-yellow-800">
                  Ouvrez la caisse avant de traiter un dépôt ou un retrait.
                </div>
              )}
              {selectedAccount ? (
                <div className="rounded-md bg-gray-50 p-3">
                  <p className="text-sm font-medium text-gray-900">{selectedAccount.account_number}</p>
                  <p className="text-sm text-gray-500">
                    {selectedAccount.client?.first_name} {selectedAccount.client?.last_name}
                  </p>
                  <p className="mt-1 text-sm text-gray-700">Solde: {formatCurrency(selectedAccount.balance)}</p>
                  {!identityConfirmed && (
                    <p className="mt-2 text-xs text-amber-700">
                      Confirmez la vérification d'identité dans Résultats.
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500">Recherchez puis sélectionnez un compte.</p>
              )}
              <select
                className="input-field"
                value={transactionForm.type}
                onChange={(e) => setTransactionForm({ ...transactionForm, type: e.target.value })}
              >
                {canDeposit && <option value="deposit">Dépôt</option>}
                {canWithdraw && <option value="withdraw">Retrait</option>}
              </select>
              <input className="input-field" type="number" min="0" step="0.01" placeholder="Montant" value={transactionForm.amount} onChange={(e) => setTransactionForm({ ...transactionForm, amount: e.target.value })} />
              <input className="input-field" placeholder="Référence reçue" value={transactionForm.reference} onChange={(e) => setTransactionForm({ ...transactionForm, reference: e.target.value })} />
              <textarea className="input-field" rows={3} placeholder="Description" value={transactionForm.description} onChange={(e) => setTransactionForm({ ...transactionForm, description: e.target.value })} />
              <button className="btn-primary w-full" type="submit" disabled={!session || !selectedAccount || !transactionForm.amount || !identityConfirmed}>
                Valider la transaction
              </button>
            </form>
          </div>
        </div>
      )}

      {activeTab === 'daily' && (
        <div className="space-y-6">
          <div className="card p-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <CalendarDaysIcon className="h-6 w-6 text-gray-400" />
              <input className="input-field max-w-xs" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div className="flex gap-3">
              <button type="button" className="btn-secondary" onClick={() => handleExportReport('daily', 'xlsx')}>
                Excel
              </button>
              <button type="button" className="btn-secondary" onClick={() => handleExportReport('daily', 'pdf')}>
                PDF
              </button>
            </div>
          </div>
          {dailyReport && <ReportSummary report={dailyReport} formatCurrency={formatCurrency} />}
        </div>
      )}

      {activeTab === 'monthly' && (
        <div className="space-y-6">
          <div className="card p-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <CalendarDaysIcon className="h-6 w-6 text-gray-400" />
              <input className="input-field max-w-xs" type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
            </div>
            <div className="flex gap-3">
              <button type="button" className="btn-secondary" onClick={() => handleExportReport('monthly', 'xlsx')}>
                Excel
              </button>
              <button type="button" className="btn-secondary" onClick={() => handleExportReport('monthly', 'pdf')}>
                PDF
              </button>
            </div>
          </div>
          {monthlyReport && <ReportSummary report={monthlyReport} formatCurrency={formatCurrency} monthly />}
        </div>
      )}
    </div>
  );
};

const ClientVerificationCard = ({ account, formatCurrency, identityConfirmed, setIdentityConfirmed }) => {
  const client = account.client || {};
  const verification = account.identity_verification || {};
  const fullName = `${client.first_name || ''} ${client.last_name || ''}`.trim() || 'Client inconnu';

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-4">
      <div className="flex items-start gap-4">
        <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-full bg-white border border-gray-200 flex items-center justify-center">
          {client.photo_url ? (
            <img src={client.photo_url} alt={fullName} className="h-full w-full object-cover" />
          ) : (
            <UserCircleIcon className="h-16 w-16 text-gray-300" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900">{fullName}</p>
          <p className="text-xs text-gray-500">Client: {client.client_number || '-'}</p>
          <p className="text-xs text-gray-500">Compte: {account.account_number}</p>
          <p className="mt-1 text-sm font-medium text-gray-900">Solde: {formatCurrency(account.balance)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3">
        <div className="flex items-start gap-3 rounded-md bg-white p-3 border border-gray-100">
          <IdentificationIcon className="h-5 w-5 text-gray-400 mt-0.5" />
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase">Pièce d'identité</p>
            <p className="text-sm text-gray-900">{client.national_id || 'Non renseignée'}</p>
            <p className="text-xs text-gray-500">{client.date_of_birth ? `Né(e) le ${new Date(client.date_of_birth).toLocaleDateString('fr-FR')}` : 'Date de naissance non renseignée'}</p>
          </div>
        </div>

        <div className="flex items-start gap-3 rounded-md bg-white p-3 border border-gray-100">
          <DocumentCheckIcon className="h-5 w-5 text-gray-400 mt-0.5" />
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase">Contacts et adresse</p>
            <p className="text-sm text-gray-900">{client.phone || 'Téléphone non renseigné'}</p>
            <p className="text-xs text-gray-500">{client.address || 'Adresse non renseignée'}</p>
          </div>
        </div>

        <div className="flex items-start gap-3 rounded-md bg-white p-3 border border-gray-100">
          <PencilSquareIcon className="h-5 w-5 text-gray-400 mt-0.5" />
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium text-gray-500 uppercase">Signature</p>
            {client.signature_url ? (
              <img src={client.signature_url} alt="Signature client" className="mt-2 max-h-16 max-w-full rounded border border-gray-200 bg-white object-contain" />
            ) : (
              <p className="text-sm text-gray-900">Signature non disponible</p>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-md bg-white p-3 border border-gray-100">
        <p className="text-xs font-medium text-gray-500 uppercase">Contrôles à faire</p>
        <ul className="mt-2 space-y-1">
          {(verification.required_checks || []).map((check) => (
            <li key={check} className="text-xs text-gray-600">- {check}</li>
          ))}
        </ul>
        <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
          <span className={verification.has_photo ? 'text-green-700' : 'text-gray-500'}>Photo: {verification.has_photo ? 'oui' : 'non'}</span>
          <span className={verification.has_signature ? 'text-green-700' : 'text-gray-500'}>Signature: {verification.has_signature ? 'oui' : 'non'}</span>
          <span className={verification.has_identity_document ? 'text-green-700' : 'text-gray-500'}>Document: {verification.has_identity_document ? 'oui' : 'non'}</span>
        </div>
      </div>

      {client.identity_document_url && (
        <a href={client.identity_document_url} target="_blank" rel="noreferrer" className="inline-flex text-sm text-primary-600 hover:text-primary-900">
          Voir le document d'identité
        </a>
      )}

      <label className="flex items-start gap-3 rounded-md bg-green-50 p-3">
        <input
          type="checkbox"
          checked={identityConfirmed}
          onChange={(e) => setIdentityConfirmed(e.target.checked)}
          className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <span className="text-sm text-green-900">
          J'ai vérifié l'identité du client avant de valider la transaction.
        </span>
      </label>
    </div>
  );
};

const ReportSummary = ({ report, formatCurrency, monthly = false }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
      <div className="card p-5"><p className="text-sm text-gray-500">Entrées</p><p className="text-xl font-semibold text-green-700">{formatCurrency(report.total_in)}</p></div>
      <div className="card p-5"><p className="text-sm text-gray-500">Sorties</p><p className="text-xl font-semibold text-red-700">{formatCurrency(report.total_out)}</p></div>
      <div className="card p-5"><p className="text-sm text-gray-500">Net caisse</p><p className="text-xl font-semibold text-gray-900">{formatCurrency(report.net_cash)}</p></div>
      <div className="card p-5"><p className="text-sm text-gray-500">Sessions ouvertes</p><p className="text-xl font-semibold text-gray-900">{report.open_sessions ?? 0}</p></div>
    </div>

    {monthly && report.daily?.length > 0 && (
      <div className="card p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Résumé par jour</h3>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {report.daily.map((day) => (
            <div key={day.date} className="border border-gray-200 rounded-lg p-4">
              <p className="font-medium text-gray-900">{new Date(day.date).toLocaleDateString('fr-FR')}</p>
              <p className="text-sm text-gray-500">Entrées: {formatCurrency(day.total_in)} · Sorties: {formatCurrency(day.total_out)}</p>
            </div>
          ))}
        </div>
      </div>
    )}

    <div className="card p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Par type de mouvement</h3>
      <div className="space-y-2">
        {(report.by_type || []).map((item) => (
          <div key={item.movement_type} className="flex justify-between border-b border-gray-100 py-2">
            <span className="text-sm text-gray-600">{item.movement_type} ({item.count})</span>
            <span className="text-sm font-medium text-gray-900">{formatCurrency(item.total)}</span>
          </div>
        ))}
      </div>
    </div>

    {report.sessions?.length > 0 && (
      <div className="card p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Ouvertures globales</h3>
        <div className="space-y-2">
          {report.sessions.map((session) => (
            <div key={session.id} className="flex items-center justify-between border-b border-gray-100 py-2">
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {session.cash_register_code || 'Caisse'} - {session.cash_register_name || 'Non assignée'}
                </p>
                <p className="text-xs text-gray-500">
                  Ouverte le {new Date(session.opened_at).toLocaleString('fr-FR')}
                </p>
              </div>
              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${session.status === 'open' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                {session.status === 'open' ? 'Ouverte' : 'Fermée'}
              </span>
            </div>
          ))}
        </div>
      </div>
    )}

    {report.by_cashier?.length > 0 && (
      <div className="card p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Par caissier</h3>
        <div className="overflow-hidden border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Caissier</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Entrées</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Sorties</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Net</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {report.by_cashier.map((cashier) => (
                <tr key={cashier.user_id || cashier.username}>
                  <td className="px-4 py-3 text-sm text-gray-900">{cashier.cashier_name || cashier.username}</td>
                  <td className="px-4 py-3 text-sm text-right text-green-700">{formatCurrency(cashier.total_in)}</td>
                  <td className="px-4 py-3 text-sm text-right text-red-700">{formatCurrency(cashier.total_out)}</td>
                  <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">{formatCurrency(cashier.net_cash)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}

    {report.by_agency?.length > 0 && (
      <div className="card p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Par agence</h3>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {report.by_agency.map((agency) => (
            <div key={agency.agency_id || agency.agency_name} className="border border-gray-200 rounded-lg p-4">
              <p className="font-medium text-gray-900">{agency.agency_name}</p>
              <p className="text-sm text-gray-500">
                Entrées: {formatCurrency(agency.total_in)} · Sorties: {formatCurrency(agency.total_out)} · Net: {formatCurrency(agency.net_cash)}
              </p>
            </div>
          ))}
        </div>
      </div>
    )}

    {report.by_register?.length > 0 && (
      <div className="card p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Ouvertures par caisse</h3>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {report.by_register.map((register) => (
            <div key={register.cash_register_id || register.cash_register_name} className="border border-gray-200 rounded-lg p-4">
              <p className="font-medium text-gray-900">{register.cash_register_code} - {register.cash_register_name}</p>
              <p className="text-sm text-gray-500">
                Ouvertes: {register.open_sessions} · Fermées: {register.closed_sessions}
              </p>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

export default CashRegister;
